import { useState, useMemo, useEffect } from "react";

// ─────────────────────────────────────────────────────────────
// Sourcing — Vendor Master + Quote Comparison
// From the Leather Sourcing Manager workbook.
// Landed = (FOB + GST + Freight + Packaging) × (1 + wastage)
// Margin = (MRP − Landed) / MRP  ·  Var = (FOB − Target) / Target
// ─────────────────────────────────────────────────────────────

const PROXY = "https://sellsync-server.onrender.com/api";
const getToken = () => localStorage.getItem("sellsync_token");
const api = async (path, opts = {}) => {
  const res = await fetch((process.env.REACT_APP_API_URL || PROXY) + path, {
    ...opts,
    headers: { "Content-Type": "application/json", ...(getToken() ? { Authorization: "Bearer " + getToken() } : {}), ...opts.headers },
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || "Request failed");
  return data;
};

const INR = (n) => "₹" + Math.round(n || 0).toLocaleString("en-IN");
const INR2 = (n) => "₹" + Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const num = (v) => (isNaN(parseFloat(v)) ? 0 : parseFloat(v));
const pct = (n) => (n * 100).toFixed(1) + "%";

const LS_VENDORS = "3june_sourcing_vendors";
const LS_QUOTES  = "3june_sourcing_quotes";
const LS_ASSUME  = "3june_sourcing_assumptions";

const load = (k, d) => { try { const v = JSON.parse(localStorage.getItem(k) || "null"); return v ?? d; } catch { return d; } };

const CITIES = ["Kanpur", "Kolkata", "Chennai", "Mumbai", "Jaipur", "Delhi NCR", "Agra", "Other"];
const STATUSES = ["Prospect", "Sampling", "Approved", "On Hold", "Blacklisted"];
const CATEGORIES = ["Leather Belt", "Handbag - Tote", "Handbag - Sling", "Handbag - Shoulder", "Handbag - Crossbody", "Wallet", "Other"];

const S = {
  card:  { background: "#fff", borderRadius: 12, border: "1px solid #e5e7eb", overflow: "hidden", marginBottom: 16 },
  hdr:   { background: "#0F1A0F", color: "#fff", padding: "12px 18px", fontWeight: 700, fontSize: 14, display: "flex", justifyContent: "space-between", alignItems: "center" },
  th:    { padding: "9px 12px", textAlign: "left", fontSize: 10, fontWeight: 700, letterSpacing: "0.4px", textTransform: "uppercase", color: "#9ca3af", borderBottom: "1px solid #e5e7eb", whiteSpace: "nowrap", background: "#f9fafb" },
  td:    { padding: "8px 12px", borderBottom: "1px solid #f3f4f6", fontSize: 12.5, color: "#374151", whiteSpace: "nowrap" },
  input: { border: "1px solid #d1d5db", borderRadius: 6, padding: "6px 9px", fontSize: 13, fontFamily: "inherit", width: "100%" },
  inputN:{ border: "1px solid #e0d5c8", borderRadius: 6, padding: "5px 8px", fontSize: 12.5, fontFamily: "inherit", background: "#fffdf8", width: 80, textAlign: "right", fontWeight: 600 },
  btn:   (bg = "#F47920") => ({ background: bg, color: "#fff", border: "none", borderRadius: 8, padding: "9px 16px", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }),
  btnOut:{ background: "#fff", color: "#374151", border: "1px solid #d1d5db", borderRadius: 8, padding: "8px 14px", fontSize: 12, fontWeight: 500, cursor: "pointer", fontFamily: "inherit" },
  tab:   (on) => ({ padding: "9px 16px", borderRadius: 8, border: "1px solid " + (on ? "#F47920" : "#e5e7eb"), background: on ? "#fff7ed" : "#fff", color: on ? "#F47920" : "#6b7280", fontWeight: on ? 700 : 500, fontSize: 13, cursor: "pointer", fontFamily: "inherit" }),
  pill:  (bg, fg) => ({ display: "inline-block", padding: "2px 9px", borderRadius: 20, fontSize: 11, fontWeight: 600, background: bg, color: fg }),
};

const statusPill = (s) => {
  const map = { Approved: ["#f0fdf4", "#15803d"], Sampling: ["#fff7ed", "#d97706"], Prospect: ["#eff6ff", "#2563eb"], "On Hold": ["#f9fafb", "#6b7280"], Blacklisted: ["#fef2f2", "#dc2626"] };
  const [bg, fg] = map[s] || ["#f9fafb", "#6b7280"];
  return <span style={S.pill(bg, fg)}>{s}</span>;
};

export default function PageSourcing({ toast }) {
  const [tab, setTab] = useState("vendors");
  const [vendors, setVendors] = useState([]);
  const [loadingV, setLoadingV] = useState(true);
  const [quotes, setQuotes] = useState(() => load(LS_QUOTES, []));
  const [assume, setAssume] = useState(() => load(LS_ASSUME, { gst: 18, freight: 25, packaging: 18, wastage: 2 }));

  const loadVendors = async () => {
    setLoadingV(true);
    try {
      const data = await api("/suppliers");
      // Map backend supplier → Sourcing vendor shape (keep sourcing-specific fields if present)
      const mapped = (data.suppliers || []).map((s) => ({
        _id: s._id, id: s._id, name: s.name || "", contact: s.company || s.contactPerson || "",
        phone: s.phone || s.whatsapp || "", email: s.email || "", gstin: s.gstin || "",
        city: s.city || "Other", focus: s.focus || "Leather Belt", status: s.status || "Prospect",
        notes: s.notes || "",
      }));
      setVendors(mapped);
    } catch (e) { toast("Could not load vendors: " + e.message, "error"); }
    setLoadingV(false);
  };
  useEffect(() => { loadVendors(); }, []);
  useEffect(() => { localStorage.setItem(LS_QUOTES, JSON.stringify(quotes)); }, [quotes]);
  useEffect(() => { localStorage.setItem(LS_ASSUME, JSON.stringify(assume)); }, [assume]);

  // ── Vendor CRUD — persists to backend /suppliers (shared with Purchase Orders) ──
  const [savingRow, setSavingRow] = useState(null);
  const addVendor = async () => {
    try {
      const created = await api("/suppliers", { method: "POST", body: JSON.stringify({
        name: "New Vendor", company: "", city: "Kanpur", focus: "Leather Belt", status: "Prospect",
      })});
      await loadVendors();
      toast("Vendor added — fill in the details", "success");
    } catch (e) { toast("Could not add vendor: " + e.message, "error"); }
  };
  // Update a field locally (instant), then save to backend on blur
  const updVendor = (i, k, val) => setVendors((v) => v.map((x, j) => j === i ? { ...x, [k]: val } : x));
  const saveVendor = async (vendor) => {
    if (!vendor._id) return;
    setSavingRow(vendor._id);
    try {
      await api("/suppliers/" + vendor._id, { method: "PUT", body: JSON.stringify({
        name: vendor.name, company: vendor.contact, phone: vendor.phone, email: vendor.email,
        gstin: vendor.gstin, city: vendor.city, focus: vendor.focus, status: vendor.status, notes: vendor.notes,
      })});
    } catch (e) { toast("Save failed: " + e.message, "error"); }
    setSavingRow(null);
  };
  const delVendor = async (i) => {
    const v = vendors[i];
    if (!window.confirm("Delete this vendor? It will also be removed from Purchase Orders.")) return;
    try { await api("/suppliers/" + v._id, { method: "DELETE" }); await loadVendors(); toast("Vendor deleted", "success"); }
    catch (e) { toast("Delete failed: " + e.message, "error"); }
  };

  // ── Quote CRUD ──
  const addQuote = () => setQuotes((q) => [...q, {
    id: "Q" + String(q.length + 1).padStart(3, "0"), date: new Date().toISOString().split("T")[0],
    product: "", vendorId: vendors[0]?._id || "", vendorName: vendors[0]?.name || "",
    qty: 500, fob: 0, target: 0, mrp: 0, notes: "",
  }]);
  const updQuote = (i, k, val) => setQuotes((q) => q.map((x, j) => {
    if (j !== i) return x;
    const next = { ...x, [k]: val };
    if (k === "vendorId") next.vendorName = vendors.find((v) => v._id === val)?.name || "";
    return next;
  }));
  const delQuote = (i) => setQuotes((q) => q.filter((_, j) => j !== i));

  // ── Landed cost + margin per quote ──
  const computed = useMemo(() => {
    const rows = quotes.map((q) => {
      const fob = num(q.fob);
      const gstAmt = fob * num(assume.gst) / 100;
      const landed = (fob + gstAmt + num(assume.freight) + num(assume.packaging)) * (1 + num(assume.wastage) / 100);
      const target = num(q.target);
      const variance = target > 0 ? (fob - target) / target : 0;
      const mrp = num(q.mrp);
      const margin = mrp > 0 ? (mrp - landed) / mrp : 0;
      return { ...q, gstAmt, landed, variance, margin };
    });
    // Cheapest landed per product name
    const cheapestByProduct = {};
    rows.forEach((r) => {
      const key = (r.product || "").toLowerCase().trim();
      if (!key) return;
      if (!cheapestByProduct[key] || r.landed < cheapestByProduct[key]) cheapestByProduct[key] = r.landed;
    });
    return rows.map((r) => ({ ...r, isCheapest: r.product && r.landed === cheapestByProduct[(r.product || "").toLowerCase().trim()] }));
  }, [quotes, assume]);

  // ── Import from Excel ──
  const importExcel = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    try {
      const XLSX = await import("xlsx");
      const wb = XLSX.read(await file.arrayBuffer());
      // Vendor Master
      const vm = wb.Sheets["Vendor Master"];
      if (vm) {
        const rows = XLSX.utils.sheet_to_json(vm, { header: 1, defval: "" });
        const hdrIdx = rows.findIndex((r) => (r[0] || "").toString().toLowerCase().includes("vendor id"));
        if (hdrIdx >= 0) {
          const imported = rows.slice(hdrIdx + 1).filter((r) => r[0]).map((r) => ({
            id: r[0], name: r[1] || "", contact: r[2] || "", phone: r[3] || "", city: r[5] || "Other",
            focus: r[6] || "Leather Belt", status: "Prospect", notes: "",
          }));
          if (imported.length) {
            let ok = 0;
            for (const v of imported) {
              try { await api("/suppliers", { method: "POST", body: JSON.stringify({
                name: v.name, company: v.contact, phone: v.phone, city: v.city, focus: v.focus, status: "Prospect",
              })}); ok++; } catch {}
            }
            await loadVendors();
            toast(`Imported ${ok} vendors ✓`, "success");
          }
        }
      }
      e.target.value = "";
    } catch (err) { toast("Import failed: " + err.message, "error"); }
  };

  return (
    <div style={{ maxWidth: 1100, margin: "0 auto" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14, flexWrap: "wrap", gap: 10 }}>
        <div>
          <div style={{ fontSize: 20, fontWeight: 800, color: "#0F1A0F" }}>🏭 Sourcing &amp; Vendors</div>
          <div style={{ fontSize: 12, color: "#8a7a6a" }}>Manufacturer directory · RFQ landed-cost &amp; margin comparison</div>
        </div>
        <label style={{ ...S.btnOut, cursor: "pointer" }}>
          📥 Import from Excel
          <input type="file" accept=".xlsx,.xls" onChange={importExcel} style={{ display: "none" }} />
        </label>
      </div>

      <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
        <button style={S.tab(tab === "vendors")} onClick={() => setTab("vendors")}>🏭 Vendors ({vendors.length})</button>
        <button style={S.tab(tab === "quotes")} onClick={() => setTab("quotes")}>💰 Quote Comparison ({quotes.length})</button>
      </div>

      {/* ── VENDORS ── */}
      {tab === "vendors" && (
        <div style={S.card}>
          <div style={S.hdr}><span>🏭 Vendor Master</span><button style={S.btn()} onClick={addVendor}>+ Add Vendor</button></div>
          {loadingV ? (
            <div style={{ padding: 40, textAlign: "center", color: "#9ca3af", fontSize: 13 }}>Loading vendors…</div>
          ) : vendors.length === 0 ? (
            <div style={{ padding: 40, textAlign: "center", color: "#9ca3af", fontSize: 13 }}>
              No vendors yet. Click "+ Add Vendor" or import your Excel workbook.<br/>
              <span style={{ fontSize: 11 }}>These are the same vendors used when creating Purchase Orders.</span>
            </div>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead><tr>{["Name", "Company", "Phone", "City", "Focus", "Status", ""].map((h) => <th key={h} style={S.th}>{h}</th>)}</tr></thead>
                <tbody>
                  {vendors.map((v, i) => (
                    <tr key={v._id || i} style={{ background: savingRow === v._id ? "#fffdf5" : "transparent" }}>
                      <td style={S.td}><input style={{ ...S.input, minWidth: 130 }} value={v.name} placeholder="Vendor name" onChange={(e) => updVendor(i, "name", e.target.value)} onBlur={() => saveVendor(v)} /></td>
                      <td style={S.td}><input style={{ ...S.input, minWidth: 100 }} value={v.contact} placeholder="Company" onChange={(e) => updVendor(i, "contact", e.target.value)} onBlur={() => saveVendor(v)} /></td>
                      <td style={S.td}><input style={{ ...S.input, minWidth: 110 }} value={v.phone} placeholder="Phone" onChange={(e) => updVendor(i, "phone", e.target.value)} onBlur={() => saveVendor(v)} /></td>
                      <td style={S.td}><select style={S.input} value={v.city} onChange={(e) => { updVendor(i, "city", e.target.value); saveVendor({ ...v, city: e.target.value }); }}>{CITIES.map((c) => <option key={c}>{c}</option>)}</select></td>
                      <td style={S.td}><select style={S.input} value={v.focus} onChange={(e) => { updVendor(i, "focus", e.target.value); saveVendor({ ...v, focus: e.target.value }); }}>{CATEGORIES.map((c) => <option key={c}>{c}</option>)}</select></td>
                      <td style={S.td}><select style={{ ...S.input, minWidth: 100 }} value={v.status} onChange={(e) => { updVendor(i, "status", e.target.value); saveVendor({ ...v, status: e.target.value }); }}>{STATUSES.map((c) => <option key={c}>{c}</option>)}</select></td>
                      <td style={S.td}><button style={{ ...S.btnOut, padding: "3px 8px", color: "#dc2626", borderColor: "#fecaca" }} onClick={() => delVendor(i)}>Del</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── QUOTE COMPARISON ── */}
      {tab === "quotes" && (
        <>
          {/* Assumptions */}
          <div style={S.card}>
            <div style={S.hdr}><span>⚙️ Cost Assumptions</span><span style={{ fontSize: 11, color: "rgba(255,255,255,0.6)" }}>Applied to every quote</span></div>
            <div style={{ display: "flex", gap: 20, padding: "14px 18px", flexWrap: "wrap" }}>
              {[["GST %", "gst"], ["Freight ₹/pc", "freight"], ["Packaging ₹/pc", "packaging"], ["Wastage %", "wastage"]].map(([lbl, k]) => (
                <div key={k} style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  <span style={{ fontSize: 11, color: "#9ca3af", fontWeight: 600 }}>{lbl}</span>
                  <input style={S.inputN} type="text" inputMode="decimal" value={assume[k]}
                    onChange={(e) => setAssume({ ...assume, [k]: e.target.value.replace(/[^0-9.]/g, "") })} />
                </div>
              ))}
            </div>
          </div>

          <div style={S.card}>
            <div style={S.hdr}><span>💰 Quote Comparison — Landed Cost &amp; Margin</span><button style={S.btn()} onClick={addQuote} disabled={vendors.length === 0}>+ Add Quote</button></div>
            {vendors.length === 0 ? (
              <div style={{ padding: 40, textAlign: "center", color: "#9ca3af", fontSize: 13 }}>Add vendors first, then compare their quotes here.</div>
            ) : quotes.length === 0 ? (
              <div style={{ padding: 40, textAlign: "center", color: "#9ca3af", fontSize: 13 }}>No quotes yet. Click "+ Add Quote" to compare vendor prices.</div>
            ) : (
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead><tr>{["Product", "Vendor", "Qty", "FOB ₹", "Target ₹", "MRP ₹", "GST ₹", "Landed ₹", "Var vs Target", "Margin @ MRP", "Best?", ""].map((h) => <th key={h} style={S.th}>{h}</th>)}</tr></thead>
                  <tbody>
                    {computed.map((r, i) => (
                      <tr key={i} style={{ background: r.isCheapest ? "#f0fdf4" : (i % 2 ? "#fafafa" : "#fff") }}>
                        <td style={S.td}><input style={{ ...S.input, minWidth: 140 }} value={r.product} placeholder="Product" onChange={(e) => updQuote(i, "product", e.target.value)} /></td>
                        <td style={S.td}><select style={{ ...S.input, minWidth: 120 }} value={r.vendorId} onChange={(e) => updQuote(i, "vendorId", e.target.value)}>{vendors.map((v) => <option key={v._id} value={v._id}>{v.name || "Unnamed"}</option>)}</select></td>
                        <td style={S.td}><input style={S.inputN} value={r.qty} onChange={(e) => updQuote(i, "qty", e.target.value.replace(/[^0-9.]/g, ""))} /></td>
                        <td style={S.td}><input style={S.inputN} value={r.fob} onChange={(e) => updQuote(i, "fob", e.target.value.replace(/[^0-9.]/g, ""))} /></td>
                        <td style={S.td}><input style={S.inputN} value={r.target} onChange={(e) => updQuote(i, "target", e.target.value.replace(/[^0-9.]/g, ""))} /></td>
                        <td style={S.td}><input style={S.inputN} value={r.mrp} onChange={(e) => updQuote(i, "mrp", e.target.value.replace(/[^0-9.]/g, ""))} /></td>
                        <td style={S.td}>{INR(r.gstAmt)}</td>
                        <td style={{ ...S.td, fontWeight: 700 }}>{INR2(r.landed)}</td>
                        <td style={{ ...S.td, color: r.variance <= 0 ? "#15803d" : "#dc2626", fontWeight: 600 }}>{r.target > 0 ? (r.variance > 0 ? "+" : "") + pct(r.variance) : "—"}</td>
                        <td style={{ ...S.td, color: r.margin >= 0.35 ? "#15803d" : "#dc2626", fontWeight: 600 }}>{r.mrp > 0 ? pct(r.margin) : "—"}</td>
                        <td style={S.td}>{r.isCheapest && <span style={S.pill("#dcfce7", "#15803d")}>✓ Best</span>}</td>
                        <td style={S.td}><button style={{ ...S.btnOut, padding: "3px 8px", color: "#dc2626", borderColor: "#fecaca" }} onClick={() => delQuote(i)}>Del</button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {quotes.length > 0 && (
              <div style={{ padding: "10px 18px", fontSize: 11, color: "#9ca3af", borderTop: "1px solid #f3f4f6" }}>
                Landed = (FOB + GST + Freight + Packaging) × (1 + wastage). Green row = cheapest landed cost for that product. Margin below 35% shows red.
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
